import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, Search, Loader2, Paperclip, ChevronLeft, ChevronRight,
  CheckCircle2, XCircle, Calendar, Filter, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  invoice_send: 'Invoice',
  quote_send: 'Quote',
  statement_send: 'Statement',
  order_shipped: 'Shipped',
  ready_for_pickup: 'Pickup',
  payment_receipt: 'Payment',
  invoice_reminder: 'Reminder',
};

const TYPE_COLORS = {
  invoice_send: 'bg-blue-50 text-blue-700 border-blue-200',
  quote_send: 'bg-purple-50 text-purple-700 border-purple-200',
  statement_send: 'bg-amber-50 text-amber-800 border-amber-200',
  order_shipped: 'bg-teal-50 text-teal-700 border-teal-200',
  ready_for_pickup: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  payment_receipt: 'bg-green-50 text-green-700 border-green-200',
  invoice_reminder: 'bg-orange-50 text-orange-700 border-orange-200',
};

const EmailHistory = () => {
  const now = new Date();
  const oneYearAgo = new Date(now);
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

  const [fromDate, setFromDate] = useState(oneYearAgo.toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(now.toISOString().slice(0, 10));
  const [recipient, setRecipient] = useState('');
  const [templateType, setTemplateType] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableTypes, setAvailableTypes] = useState([]);

  const fetchData = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      if (recipient) params.set('recipient', recipient);
      if (templateType) params.set('template_type', templateType);
      if (status) params.set('status', status);
      params.set('page', p);
      params.set('limit', 20);

      const res = await fetch(`${API_URL}/api/email-history?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load');
      const result = await res.json();
      setData(result);
      if (result.available_types) setAvailableTypes(result.available_types);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, recipient, templateType, status, page]);

  useEffect(() => { fetchData(1); }, []);  // eslint-disable-line

  const handleSearch = () => { setPage(1); fetchData(1); };
  const handleClear = () => {
    setRecipient('');
    setTemplateType('');
    setStatus('');
    setFromDate(oneYearAgo.toISOString().slice(0, 10));
    setToDate(now.toISOString().slice(0, 10));
    setPage(1);
    setTimeout(() => fetchData(1), 0);
  };
  const goPage = (p) => { setPage(p); fetchData(p); };

  const setQuickRange = (days) => {
    const to = new Date();
    const from = new Date(to);
    from.setDate(from.getDate() - days);
    setFromDate(from.toISOString().slice(0, 10));
    setToDate(to.toISOString().slice(0, 10));
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const fmtTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-5" data-testid="email-history-page">
      {/* Filters */}
      <div className="bg-white rounded-lg border p-5">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Mail className="w-5 h-5 text-[#8B7355]" />
          Email History
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Date Range */}
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="text-xs font-medium text-gray-500 mb-1 block">From</label>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                  className="w-full pl-8 pr-2 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#8B7355]/30 focus:border-[#8B7355]"
                  data-testid="email-history-from-date" />
              </div>
            </div>
            <div className="flex-1">
              <label className="text-xs font-medium text-gray-500 mb-1 block">To</label>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                  className="w-full pl-8 pr-2 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#8B7355]/30 focus:border-[#8B7355]"
                  data-testid="email-history-to-date" />
              </div>
            </div>
          </div>

          {/* Recipient */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Recipient</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input type="text" value={recipient} onChange={e => setRecipient(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Search by email address..."
                className="w-full pl-8 pr-2 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#8B7355]/30 focus:border-[#8B7355]"
                data-testid="email-history-recipient" />
            </div>
          </div>

          {/* Type + Status */}
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="text-xs font-medium text-gray-500 mb-1 block">Type</label>
              <div className="relative">
                <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <select value={templateType} onChange={e => setTemplateType(e.target.value)}
                  className="w-full pl-8 pr-8 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#8B7355]/30 focus:border-[#8B7355] appearance-none bg-white"
                  data-testid="email-history-type-filter">
                  <option value="">All Types</option>
                  {availableTypes.map(t => (
                    <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex-1">
              <label className="text-xs font-medium text-gray-500 mb-1 block">Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#8B7355]/30 focus:border-[#8B7355] appearance-none bg-white"
                data-testid="email-history-status-filter">
                <option value="">All</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Quick Ranges + Actions */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex gap-2">
            {[{ label: '30 Days', days: 30 }, { label: '90 Days', days: 90 }, { label: '1 Year', days: 365 }].map(p => (
              <button key={p.label} onClick={() => setQuickRange(p.days)}
                data-testid={`email-history-quick-${p.days}`}
                className="px-3 py-1.5 text-xs rounded-full border border-[#8B7355]/30 text-[#8B7355] hover:bg-[#8B7355]/10 transition-colors">
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleClear} data-testid="email-history-clear-btn">
              <X className="w-3.5 h-3.5 mr-1" /> Clear
            </Button>
            <Button size="sm" onClick={handleSearch} disabled={loading}
              className="bg-[#8B7355] hover:bg-[#6B5B4A] text-white" data-testid="email-history-search-btn">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Search className="w-3.5 h-3.5 mr-1" />}
              Search
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="email-history-summary">
          <div className="bg-white rounded-lg border p-4 text-center">
            <p className="text-2xl font-bold text-gray-800" data-testid="summary-total">{data.summary.total}</p>
            <p className="text-xs text-gray-500 mt-1">Total Emails</p>
          </div>
          <div className="bg-white rounded-lg border p-4 text-center">
            <p className="text-2xl font-bold text-green-600" data-testid="summary-sent">{data.summary.sent}</p>
            <p className="text-xs text-gray-500 mt-1">Sent</p>
          </div>
          <div className="bg-white rounded-lg border p-4 text-center">
            <p className="text-2xl font-bold text-red-500" data-testid="summary-failed">{data.summary.failed}</p>
            <p className="text-xs text-gray-500 mt-1">Failed</p>
          </div>
          <div className="bg-white rounded-lg border p-4 text-center">
            <p className="text-2xl font-bold text-[#8B7355]" data-testid="summary-attachments">{data.summary.with_attachment}</p>
            <p className="text-xs text-gray-500 mt-1">With Attachment</p>
          </div>
        </div>
      )}

      {/* Table */}
      {data && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="email-history-table">
              <thead>
                <tr className="bg-[#f5f5f0] border-b">
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Recipient</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Subject</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Type</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-700">Attach</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((log, i) => (
                  <tr key={log.id || i} className="border-b hover:bg-gray-50/60" data-testid={`email-row-${i}`}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <p className="font-medium text-gray-800">{fmtDate(log.sent_at)}</p>
                      <p className="text-xs text-gray-400">{fmtTime(log.sent_at)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800 truncate max-w-[200px]">{log.to_email}</p>
                      {log.cc_emails && log.cc_emails.length > 0 && (
                        <p className="text-xs text-gray-400 truncate max-w-[200px]">
                          CC: {log.cc_emails.join(', ')}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-700 truncate max-w-[280px]" title={log.subject}>{log.subject}</p>
                      {log.status === 'failed' && log.error_message && (
                        <p className="text-xs text-red-500 mt-0.5 truncate max-w-[280px]" title={log.error_message}>
                          {log.error_message}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2.5 py-0.5 text-xs font-medium rounded-full border ${TYPE_COLORS[log.template_type] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                        {TYPE_LABELS[log.template_type] || log.template_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {log.status === 'sent' ? (
                        <span className="inline-flex items-center gap-1 text-green-600" data-testid={`status-sent-${i}`}>
                          <CheckCircle2 className="w-4 h-4" />
                          <span className="text-xs">Sent</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-500" data-testid={`status-failed-${i}`}>
                          <XCircle className="w-4 h-4" />
                          <span className="text-xs">Failed</span>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {log.has_attachment && (
                        <Paperclip className="w-4 h-4 text-gray-400 mx-auto" data-testid={`attach-${i}`} />
                      )}
                    </td>
                  </tr>
                ))}
                {data.items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                      No emails match your filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data.total_pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-[#f5f5f0]/50">
              <p className="text-xs text-gray-500">
                Page {data.page} of {data.total_pages} ({data.total} emails)
              </p>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={data.page <= 1} onClick={() => goPage(data.page - 1)}
                  data-testid="email-history-prev">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {Array.from({ length: Math.min(data.total_pages, 5) }, (_, i) => {
                  const start = Math.max(1, Math.min(data.page - 2, data.total_pages - 4));
                  const p = start + i;
                  if (p > data.total_pages) return null;
                  return (
                    <Button key={p} variant={p === data.page ? 'default' : 'outline'} size="sm"
                      className={p === data.page ? 'bg-[#8B7355] hover:bg-[#6B5B4A] text-white' : ''}
                      onClick={() => goPage(p)} data-testid={`email-history-page-${p}`}>
                      {p}
                    </Button>
                  );
                })}
                <Button variant="outline" size="sm" disabled={data.page >= data.total_pages} onClick={() => goPage(data.page + 1)}
                  data-testid="email-history-next">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailHistory;
